import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  NEWS_CONFIG,
  STORAGE_KEYS,
} from "../config/newsConfig";
import {
  fetchLatestNews,
  fetchOlderNewsBatch,
  fetchSourceCatalog,
  fetchSourceCounts,
  verifyNewsApiConnection,
} from "../api/newsApi";
import {
  getNewsSortTime,
  sortNewsByPublished,
} from "../utils/newsSort";
import {
  getOrCreateUnreadCountSince,
  isNewsUnreadEligible,
} from "../utils/unreadStorage";
import {
  getSourceKey,
  normalizeSourceList,
  sortSourceListAlphabetically,
  normalizeSourceName,
} from "../utils/sourceModel";
import {
  getLatestNewsId,
  getNewsKey,
  getOldestNewsCursor,
  mergeUniqueNewsByPublished,
} from "../utils/newsModel";
import {
  readStoredJson,
  writeStoredJson,
} from "../utils/storage";

const MAX_DUPLICATE_BATCH_HOPS = 4;
const MAX_STALLED_CURSOR_ATTEMPTS = 2;
const MAX_DEFERRED_INSERT_ROWS = 500;
const CONNECTION_VERIFY_EVENT = "news-api-verify-connection";

const readDeferredInsertNews = () => {
  const stored = readStoredJson(
    STORAGE_KEYS.deferredInsertNews,
    []
  );

  return Array.isArray(stored)
    ? stored.slice(-MAX_DEFERRED_INSERT_ROWS)
    : [];
};

const persistDeferredInsertNews = (items = []) => {
  writeStoredJson(
    STORAGE_KEYS.deferredInsertNews,
    items.slice(-MAX_DEFERRED_INSERT_ROWS)
  );
};

const CONNECTION_ERROR_KINDS = new Set([
  "offline",
  "network",
  "timeout",
]);

const CONNECTION_ERROR_MESSAGES = new Set([
  "اتصال اینترنت خود را بررسی کنید",
  "ارتباط با سرور برقرار نشد",
  "پاسخ سرور بیشتر از حد معمول طول کشید",
]);

const isConnectionFailure = (error) => {
  return CONNECTION_ERROR_KINDS.has(error?.kind);
};

const isConnectionErrorMessage = (message) => {
  return CONNECTION_ERROR_MESSAGES.has(message);
};

const getOlderScopeKey = (sources = []) => {
  const normalized = sources
    .map(getSourceKey)
    .filter(Boolean)
    .sort();

  return normalized.length > 0
    ? `sources:${normalized.join("|")}`
    : "sources:all";
};

const getCursorSignature = (cursor) => {
  return `${cursor?.sortDate || ""}|${cursor?.id || 0}`;
};

const didCursorAdvance = (requestCursor, responseCursor) => {
  if (!responseCursor?.id) return false;

  if (!requestCursor?.id) {
    return true;
  }

  return (
    getCursorSignature(requestCursor) !==
    getCursorSignature(responseCursor)
  );
};

const countUnreadEligibleNewsBySource = (
  items,
  unreadCountSince
) => {
  return (items || []).reduce((counts, item) => {
    if (!isNewsUnreadEligible(item, unreadCountSince)) {
      return counts;
    }

    const source = normalizeSourceName(item?.source);

    if (!source) return counts;

    counts[source] = (counts[source] || 0) + 1;
    return counts;
  }, {});
};

export const useNewsFeed = () => {
  const [unreadCountSince] = useState(getOrCreateUnreadCountSince);
  const [allNews, setAllNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pollingError, setPollingError] = useState("");
  const [homeLoadMoreError, setHomeLoadMoreError] = useState("");
  const [sourceCatalogError, setSourceCatalogError] = useState("");
  const [sourceCountsError, setSourceCountsError] = useState("");
  const [isBrowserOffline, setIsBrowserOffline] = useState(() => {
    return typeof navigator !== "undefined" ? !navigator.onLine : false;
  });
  const [sourceCatalog, setSourceCatalog] = useState([]);
  const [sourceTotalCounts, setSourceTotalCounts] = useState({});
  const [sourceCountsRefreshVersion, setSourceCountsRefreshVersion] =
    useState(0);
  const [unreadFallbackCounts, setUnreadFallbackCounts] =
    useState({});
  const [pendingNews, setPendingNews] = useState([]);
  const [largeBacklog, setLargeBacklog] = useState(null);
  const [homeSnapshotRefreshing, setHomeSnapshotRefreshing] = useState(false);
  const [focusNewsBySource, setFocusNewsBySource] = useState({});
  /*
  |--------------------------------------------------------------------------
  | Home-feed pagination state
  |--------------------------------------------------------------------------
  |
  | Home pagination is intentionally isolated from Focus Mode pagination.
  | Each active-source combination has its own has-more flag.
  |
  */
  const [homeHasMoreByScope, setHomeHasMoreByScope] = useState({
    "sources:all": true,
  });
  const [hasMoreNewsBySource, setHasMoreNewsBySource] = useState({});
  const [checkedFocusSources, setCheckedFocusSources] = useState({});
  const [
    focusInitialErrorsBySource,
    setFocusInitialErrorsBySource,
  ] = useState({});
  const [
    focusLoadMoreErrorsBySource,
    setFocusLoadMoreErrorsBySource,
  ] = useState({});
  const [loadingFocusSources, setLoadingFocusSources] = useState({});

  const prevLinksRef = useRef(new Set());
  const loadedNewsKeysRef = useRef(new Set());
  const focusNewsKeysBySourceRef = useRef({});
  const focusFetchingSourcesRef = useRef(new Set());
  const insertCursorIdRef = useRef(0);
  const insertCatchupTargetIdRef = useRef(0);
  const insertPollHasMoreRef = useRef(false);
  const insertPollingContextRef = useRef({
    topCursor: { id: 0, sortDate: "" },
    deepestReadCursor: { id: 0, sortDate: "" },
    sources: [],
  });
  const replaceHomeSnapshotRequestTokenRef = useRef(0);
  const replaceHomeSnapshotControllerRef = useRef(null);
  const deferredInsertNewsRef = useRef(null);

  if (deferredInsertNewsRef.current === null) {
    deferredInsertNewsRef.current = readDeferredInsertNews();
  }

  useEffect(() => {
    return () => {
      replaceHomeSnapshotRequestTokenRef.current += 1;
      replaceHomeSnapshotControllerRef.current?.abort();
      replaceHomeSnapshotControllerRef.current = null;
    };
  }, []);

  /*
  |--------------------------------------------------------------------------
  | Latest-news request coordination
  |--------------------------------------------------------------------------
  |
  | React StrictMode mounts, cleans up, and mounts effects again in
  | development. A simple boolean lock can therefore reject the second real
  | initial request while the first aborted request is still finishing.
  |
  | Request tokens let a newer initial request supersede an older aborted one
  | without allowing normal polling requests to overlap.
  |
  */
  const latestRequestTokenRef = useRef(0);
  const activeLatestRequestTokenRef = useRef(null);
  const connectionUnavailableRef = useRef(false);

  /*
  |--------------------------------------------------------------------------
  | Older-news pagination state
  |--------------------------------------------------------------------------
  |
  | Each source scope keeps its own cursor and request lock.
  | A duplicate batch is allowed to advance the cursor without permanently
  | disabling Load More. Only a genuinely exhausted response or a repeatedly
  | stalled cursor closes that scope.
  |
  */
  const fetchingOlderScopesRef = useRef(new Set());
  const olderCursorByScopeRef = useRef({});
  const stalledOlderScopeCountsRef = useRef({});
  const homeLoadMoreFailureCountsRef = useRef({});
  const homeLoadMoreRetryAtRef = useRef({});
  const focusLoadMoreFailureCountsRef = useRef({});
  const focusLoadMoreRetryAtRef = useRef({});

  const setInsertPollingContext = useCallback((context = {}) => {
    const sources = Array.from(
      new Set(
        (context.sources || [])
          .map(normalizeSourceName)
          .filter(Boolean)
      )
    ).sort();

    insertPollingContextRef.current = {
      topCursor: {
        id: Number(context?.topCursor?.id) || 0,
        sortDate: String(context?.topCursor?.sortDate || "").trim(),
      },
      deepestReadCursor: {
        id: Number(context?.deepestReadCursor?.id) || 0,
        sortDate: String(
          context?.deepestReadCursor?.sortDate || ""
        ).trim(),
      },
      sources,
    };
  }, []);

  const availableSources = useMemo(() => {
    const sources = sourceCatalog
      .concat(allNews.map((item) => item.source))
      .concat(pendingNews.map((item) => item.source));

    return sortSourceListAlphabetically(sources);
  }, [allNews, pendingNews, sourceCatalog]);

  const failedFocusSources = useMemo(() => {
    return Object.keys(focusInitialErrorsBySource).reduce(
      (result, sourceKey) => {
        result[sourceKey] = Boolean(
          focusInitialErrorsBySource[sourceKey]
        );

        return result;
      },
      {}
    );
  }, [focusInitialErrorsBySource]);

  const refreshError = pollingError;

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleOffline = () => {
      connectionUnavailableRef.current = true;
      setIsBrowserOffline(true);
    };

    const requestConnectionVerification = () => {
      /*
      |-----------------------------------------------------------------------
      | `navigator.onLine` is only a hint.
      |-----------------------------------------------------------------------
      |
      | Chrome, Android WebView, DevTools and some network transitions can
      | report an outdated value or miss the `online` event entirely. We never
      | mark the app online from this event. Instead, it only asks the real API
      | probe to run immediately. A successful API response is the source of
      | truth.
      |
      */
      window.dispatchEvent(new Event(CONNECTION_VERIFY_EVENT));
    };

    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      handleOffline();
    }

    window.addEventListener("online", requestConnectionVerification);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", requestConnectionVerification);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const loadSourceCatalog = async () => {
      try {
        const sources = await fetchSourceCatalog({
          signal: controller.signal,
        });

        setSourceCatalog(sources);
        setSourceCatalogError("");
      } catch (error) {
        if (error.name !== "AbortError") {
          setSourceCatalogError(error.message || "دریافت فهرست منابع انجام نشد");
          console.error(error);
        }
      }
    };

    loadSourceCatalog();

    return () => controller.abort();
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const loadSourceCounts = async () => {
      try {
        const counts = await fetchSourceCounts({
          since: unreadCountSince,
          signal: controller.signal,
        });

        setSourceTotalCounts(counts);
        setSourceCountsError("");
      } catch (error) {
        if (error.name !== "AbortError") {
          setSourceCountsError(
            error.message || "دریافت تعداد خبرهای منابع انجام نشد"
          );
          console.error(error);
        }
      }
    };

    loadSourceCounts();

    return () => controller.abort();
  }, [sourceCountsRefreshVersion, unreadCountSince]);

  const fetchNews = useCallback(async (isInitial = false, signal) => {
    const hasActiveLatestRequest =
      activeLatestRequestTokenRef.current !== null;

    /*
    |--------------------------------------------------------------------------
    | Polling requests must never overlap.
    |--------------------------------------------------------------------------
    |
    | A newer initial request is allowed to supersede an older initial request
    | that React StrictMode has just aborted.
    |
    */
    if (!isInitial && hasActiveLatestRequest) {
      return false;
    }

    const requestToken = latestRequestTokenRef.current + 1;

    latestRequestTokenRef.current = requestToken;
    activeLatestRequestTokenRef.current = requestToken;

    /*
    |--------------------------------------------------------------------------
    | Protect an uninitialized feed from being treated as fresh news.
    |--------------------------------------------------------------------------
    |
    | If the explicit initial request was aborted, a later recovery request may
    | still use limit=100 because the cursor is empty. That response must
    | hydrate allNews and clear pendingNews; it must never become "خبر تازه".
    |
    */
    const shouldHydrateInitialFeed =
      isInitial ||
      (
        !insertCursorIdRef.current &&
        prevLinksRef.current.size === 0
      );

    try {
      if (shouldHydrateInitialFeed) {
        setLoading(true);
        setError("");

        insertCursorIdRef.current = 0;
        insertCatchupTargetIdRef.current = 0;
        insertPollHasMoreRef.current = false;
        setLargeBacklog(null);
        prevLinksRef.current = new Set();
        loadedNewsKeysRef.current = new Set();
        focusNewsKeysBySourceRef.current = {};
        focusFetchingSourcesRef.current = new Set();
        fetchingOlderScopesRef.current = new Set();
        olderCursorByScopeRef.current = {};
        stalledOlderScopeCountsRef.current = {};

        setFocusNewsBySource({});
        setUnreadFallbackCounts({});
        setHomeHasMoreByScope({
          "sources:all": true,
        });
        setHasMoreNewsBySource({});
        setCheckedFocusSources({});
        setFocusInitialErrorsBySource({});
        setFocusLoadMoreErrorsBySource({});
        homeLoadMoreFailureCountsRef.current = {};
        homeLoadMoreRetryAtRef.current = {};
        focusLoadMoreFailureCountsRef.current = {};
        focusLoadMoreRetryAtRef.current = {};
      }

      const requestAfterInsertId = insertCursorIdRef.current;

      const latestResponse = await fetchLatestNews({
        cursor: {
          id: requestAfterInsertId,
        },
        isInitial: shouldHydrateInitialFeed,
        signal,
        targetInsertId: shouldHydrateInitialFeed
          ? 0
          : insertCatchupTargetIdRef.current,
        pollingContext: insertPollingContextRef.current,
      });

      /*
      |--------------------------------------------------------------------
      | Ignore stale responses.
      |--------------------------------------------------------------------
      |
      | An older aborted StrictMode request must not overwrite the state of the
      | newer request that replaced it.
      |
      */
      if (requestToken !== latestRequestTokenRef.current) {
        return false;
      }

      const rawData = latestResponse.items || [];
      const maximumReturnedId = getLatestNewsId(rawData);

      if (shouldHydrateInitialFeed) {
        insertCursorIdRef.current = latestResponse.hasInsertWatermark
          ? latestResponse.insertWatermarkId
          : maximumReturnedId;
        insertCatchupTargetIdRef.current = 0;
        insertPollHasMoreRef.current = false;
      } else if (latestResponse.mode === "large_backlog") {
        const targetInsertId = Math.max(
          requestAfterInsertId,
          latestResponse.targetInsertId || 0,
          latestResponse.nextInsertId || 0
        );

        insertCursorIdRef.current = targetInsertId;
        insertCatchupTargetIdRef.current = 0;
        insertPollHasMoreRef.current = false;

        const deferredCandidates = [
          ...deferredInsertNewsRef.current,
          ...(latestResponse.deferredItems || []),
        ];
        const deferredKeys = new Set();

        deferredInsertNewsRef.current = deferredCandidates.filter((item) => {
          const key = getNewsKey(item);

          if (!key || deferredKeys.has(key)) return false;

          deferredKeys.add(key);
          return true;
        }).slice(-MAX_DEFERRED_INSERT_ROWS);

        persistDeferredInsertNews(
          deferredInsertNewsRef.current
        );

        setLargeBacklog({
          sessionKey: `insert:${requestAfterInsertId}:${targetInsertId}`,
          afterInsertId: requestAfterInsertId,
          targetInsertId,
          totalInserted: latestResponse.totalInserted,
          backlogThreshold: latestResponse.backlogThreshold,
          freshCount: latestResponse.freshCount,
          freshCountsBySource: latestResponse.freshCountsBySource,
          readBackfillCount: latestResponse.readBackfillCount,
          readBackfillCountsBySource:
            latestResponse.readBackfillCountsBySource,
          detectedAt: Date.now(),
        });
      } else {
        insertCursorIdRef.current = Math.max(
          insertCursorIdRef.current,
          latestResponse.nextInsertId || 0,
          maximumReturnedId
        );
        insertCatchupTargetIdRef.current = latestResponse.hasMore
          ? Math.max(0, latestResponse.targetInsertId || 0)
          : 0;
        insertPollHasMoreRef.current = Boolean(
          latestResponse.hasMore
        );
      }

      connectionUnavailableRef.current = false;
      setIsBrowserOffline(false);

      /*
      |--------------------------------------------------------------------
      | Re-open Focus initial loading after a proven connection recovery
      |--------------------------------------------------------------------
      |
      | Focus initial errors are rendered as skeletons. Once any real feed
      | request succeeds, connection-related Focus errors must be cleared so
      | the already-mounted current/neighbor pages can run onEnsureInitial
      | again and replace the skeleton with real news without a refresh.
      |
      */
      setFocusInitialErrorsBySource((previous) => {
        let didChange = false;
        const next = { ...previous };

        Object.keys(next).forEach((sourceKey) => {
          if (!isConnectionErrorMessage(next[sourceKey])) return;

          delete next[sourceKey];
          didChange = true;
        });

        return didChange ? next : previous;
      });

      if (
        !shouldHydrateInitialFeed &&
        latestResponse.mode === "large_backlog"
      ) {
        setPollingError("");
        return true;
      }

      const rawDataCount = rawData.length;

      if (shouldHydrateInitialFeed && rawDataCount > 0) {
        olderCursorByScopeRef.current["sources:all"] =
          getOldestNewsCursor(rawData);
      }

      /*
      |--------------------------------------------------------------------
      | Defer future-dated inserts instead of losing them
      |--------------------------------------------------------------------
      |
      | The insert cursor is allowed to advance past every database row. Rows
      | whose publication time is still in the future stay in a small persisted
      | queue and are re-evaluated on every poll and after a page reload. Invalid
      | rows are discarded.
      |
      */
      let data;
      let restoredPendingItems = [];
      const now = Date.now();
      const candidateItems = shouldHydrateInitialFeed
        ? deferredInsertNewsRef.current
        : [
            ...deferredInsertNewsRef.current,
            ...rawData,
          ];
      const candidates = [];
      const candidateKeys = new Set();

      candidateItems.forEach((item) => {
        const key = getNewsKey(item);

        if (!key || candidateKeys.has(key)) return;

        candidateKeys.add(key);
        candidates.push(item);
      });

      const activatedDeferredItems = sortNewsByPublished(
        candidates,
        now
      );
      const activatedDeferredKeys = new Set(
        activatedDeferredItems
          .map(getNewsKey)
          .filter(Boolean)
      );

      deferredInsertNewsRef.current = candidates.filter((item) => {
        const key = getNewsKey(item);
        const sortTime = getNewsSortTime(item);

        return (
          Boolean(key) &&
          !activatedDeferredKeys.has(key) &&
          sortTime > now
        );
      });
      persistDeferredInsertNews(
        deferredInsertNewsRef.current
      );

      if (shouldHydrateInitialFeed) {
        data = sortNewsByPublished(rawData, now);
        const initialKeys = new Set(
          data.map(getNewsKey).filter(Boolean)
        );

        restoredPendingItems = activatedDeferredItems.filter((item) => {
          const key = getNewsKey(item);
          return key && !initialKeys.has(key);
        });
      } else {
        data = activatedDeferredItems;
      }

      if (shouldHydrateInitialFeed) {
        setError("");
      } else {
        setPollingError("");
      }

      const addedItems = data.filter((item) => {
        const key = getNewsKey(item);

        return key && !loadedNewsKeysRef.current.has(key);
      });

      if (shouldHydrateInitialFeed) {
        setAllNews(data);
        setPendingNews(restoredPendingItems);
        setUnreadFallbackCounts(
          countUnreadEligibleNewsBySource(
            data,
            unreadCountSince
          )
        );
        setError("");
        setLoading(false);

        const initiallyKnownItems = [
          ...data,
          ...restoredPendingItems,
        ];

        prevLinksRef.current = new Set(
          initiallyKnownItems
            .map((item) => item.link)
            .filter(Boolean)
        );

        loadedNewsKeysRef.current = new Set(
          initiallyKnownItems
            .map(getNewsKey)
            .filter(Boolean)
        );

        /*
        |--------------------------------------------------------------------------
        | Keep Load More based on the raw API batch size.
        |--------------------------------------------------------------------------
        |
        | sortNewsByPublished may omit an item with an invalid/future date.
        | That must not make the frontend think the database has ended.
        |
        */
        setHomeHasMoreByScope((prev) => ({
          ...prev,
          "sources:all":
            rawDataCount >= NEWS_CONFIG.initialBatchSize,
        }));

        /*
        | Do not re-fetch source_counts after initial feed hydration.
        |
        | The source_counts request already starts independently on mount and
        | uses the same unreadCountSince window. Re-triggering it here produces
        | an identical second startup request without changing the model. If
        | metadata is temporarily unavailable, useUnreadCounts already falls
        | back to the initial snapshot. Explicit snapshot replacement below
        | still refreshes exact counts when the feed scope is genuinely reset.
        */
        return true;
      }

      if (addedItems.length > 0) {
        const updatedLinks = new Set(prevLinksRef.current);
        const updatedKeys = new Set(loadedNewsKeysRef.current);

        addedItems.forEach((item) => {
          updatedLinks.add(item.link);

          const key = getNewsKey(item);
          if (key) updatedKeys.add(key);
        });

        prevLinksRef.current = updatedLinks;
        loadedNewsKeysRef.current = updatedKeys;

        setPendingNews((prev) => {
          const existing = new Set(
            prev.map(getNewsKey).filter(Boolean)
          );
          const uniqueItems = addedItems.filter((item) => {
            const key = getNewsKey(item);
            return key && !existing.has(key);
          });

          return uniqueItems.length > 0
            ? [...prev, ...uniqueItems]
            : prev;
        });

        /*
        |--------------------------------------------------------------------
        | Keep fresh polling rows outside Unread until the user accepts them
        |--------------------------------------------------------------------
        |
        | addedItems are intentionally stored only in pendingNews here. The
        | bottom capsule is the user's commit action. Until that click happens,
        | neither the exact nor fallback Unread totals are allowed to change.
        |
        */
      }

      /*
      | Do not refresh source_counts from polling. The database already contains
      | pending rows, so a live recount would expose them in Unread before the
      | capsule is clicked. Initial source_counts remains the exact baseline for
      | this mounted session; accepted pending rows are added locally below.
      */
      return true;
    } catch (error) {
      if (
        requestToken !== latestRequestTokenRef.current ||
        error.name === "AbortError"
      ) {
        return false;
      }

      if (isConnectionFailure(error)) {
        connectionUnavailableRef.current = true;
        setIsBrowserOffline(true);
      }

      if (shouldHydrateInitialFeed) {
        setError(error.message || "خطای نامشخص");
        setLoading(false);
      } else {
        setPollingError(error.message || "بروزرسانی خبرها انجام نشد");
      }

      return false;
    } finally {
      if (requestToken === latestRequestTokenRef.current) {
        activeLatestRequestTokenRef.current = null;
      }
    }
  }, [unreadCountSince]);

  /*
  |--------------------------------------------------------------------------
  | Real API connectivity verification
  |--------------------------------------------------------------------------
  |
  | The app stays in the offline state until the news API itself answers.
  | `navigator.onLine` and the browser `online` event are only hints and can be
  | stale. While disconnected, a tiny no-cache request is sent every few
  | seconds. Once it succeeds, the feed is recovered immediately without a
  | page refresh.
  |
  */
  useEffect(() => {
    if (!isBrowserOffline || typeof window === "undefined") {
      return undefined;
    }

    let disposed = false;
    let timeoutId = null;
    let activeController = null;
    let isProbeRunning = false;

    const clearTimer = () => {
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    const scheduleProbe = () => {
      if (disposed) return;

      clearTimer();
      timeoutId = window.setTimeout(
        runProbe,
        NEWS_CONFIG.connectionProbeIntervalMs
      );
    };

    const runProbe = async () => {
      if (disposed || isProbeRunning) return;

      isProbeRunning = true;
      clearTimer();

      activeController?.abort();
      activeController = new AbortController();

      try {
        await verifyNewsApiConnection({
          signal: activeController.signal,
        });

        if (disposed) return;

        /*
        |--------------------------------------------------------------------
        | The API answered, so network reachability is proven.
        |--------------------------------------------------------------------
        |
        | Recover the actual feed before leaving the offline state. For an
        | empty app we hydrate the initial batch; otherwise we run a normal
        | latest-news request and preserve the mounted list.
        |
        */
        connectionUnavailableRef.current = false;

        const shouldHydrateInitialFeed =
          !insertCursorIdRef.current &&
          prevLinksRef.current.size === 0;

        const recovered = await fetchNews(
          shouldHydrateInitialFeed,
          activeController.signal
        );

        if (disposed) return;

        if (recovered || !connectionUnavailableRef.current) {
          setIsBrowserOffline(false);

          setError((current) =>
            isConnectionErrorMessage(current) ? "" : current
          );

          setPollingError((current) =>
            isConnectionErrorMessage(current) ? "" : current
          );

          return;
        }

        scheduleProbe();
      } catch (error) {
        if (disposed || error?.name === "AbortError") return;

        connectionUnavailableRef.current = true;
        setIsBrowserOffline(true);
        scheduleProbe();
      } finally {
        isProbeRunning = false;
        activeController = null;
      }
    };

    const requestImmediateProbe = () => {
      clearTimer();
      runProbe();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        requestImmediateProbe();
      }
    };

    window.addEventListener("online", requestImmediateProbe);
    window.addEventListener("focus", requestImmediateProbe);
    window.addEventListener(
      CONNECTION_VERIFY_EVENT,
      requestImmediateProbe
    );
    document.addEventListener(
      "visibilitychange",
      handleVisibilityChange
    );

    runProbe();

    return () => {
      disposed = true;
      clearTimer();
      activeController?.abort();

      window.removeEventListener("online", requestImmediateProbe);
      window.removeEventListener("focus", requestImmediateProbe);
      window.removeEventListener(
        CONNECTION_VERIFY_EVENT,
        requestImmediateProbe
      );
      document.removeEventListener(
        "visibilitychange",
        handleVisibilityChange
      );
    };
  }, [fetchNews, isBrowserOffline]);

  const fetchOlderNews = useCallback(
    async (sourceFilteredNews, sources = []) => {
      const scopeKey = getOlderScopeKey(sources);

      /*
      |--------------------------------------------------------------------------
      | Home Load More reads only Home pagination state.
      |--------------------------------------------------------------------------
      |
      | It must never read or mutate Focus Mode's hasMoreNewsBySource map.
      |
      */
      const canLoadMore =
        homeHasMoreByScope[scopeKey] !== false;

      const retryAt = homeLoadMoreRetryAtRef.current[scopeKey] || 0;

      if (
        !canLoadMore ||
        fetchingOlderScopesRef.current.has(scopeKey) ||
        Date.now() < retryAt
      ) {
        return 0;
      }

      fetchingOlderScopesRef.current.add(scopeKey);

      try {
        /*
        |--------------------------------------------------------------------------
        | Use a persistent cursor for this exact source scope.
        |--------------------------------------------------------------------------
        |
        | The cursor is advanced from the raw API response, even when every
        | returned item is already present in the frontend. This lets us pass
        | duplicate regions instead of disabling Load More permanently.
        |
        */
        let cursor =
          olderCursorByScopeRef.current[scopeKey] ||
          getOldestNewsCursor(sourceFilteredNews);

        olderCursorByScopeRef.current[scopeKey] = cursor;

        const existingKeys = new Set(loadedNewsKeysRef.current);
        const collectedItems = [];

        let hasMoreForScope = true;

        for (
          let hop = 0;
          hop < MAX_DUPLICATE_BATCH_HOPS;
          hop += 1
        ) {
          const rawData = await fetchOlderNewsBatch({
            cursor,
            sources,
            limit: NEWS_CONFIG.loadMoreBatchSize,
          });

          const rawCount = rawData.length;

          if (rawCount === 0) {
            hasMoreForScope = false;
            break;
          }

          const responseCursor = getOldestNewsCursor(rawData);
          const cursorAdvanced = didCursorAdvance(
            cursor,
            responseCursor
          );

          /*
          |--------------------------------------------------------------------------
          | A repeated cursor means the backend returned the same page.
          |--------------------------------------------------------------------------
          |
          | We retry on the next Load More call once, but stop the scope after
          | repeated stalls so a broken backend cannot create an infinite loop.
          |
          */
          if (!cursorAdvanced) {
            const stallCount =
              (stalledOlderScopeCountsRef.current[scopeKey] || 0) + 1;

            stalledOlderScopeCountsRef.current[scopeKey] =
              stallCount;

            hasMoreForScope =
              stallCount < MAX_STALLED_CURSOR_ATTEMPTS;

            break;
          }

          stalledOlderScopeCountsRef.current[scopeKey] = 0;
          olderCursorByScopeRef.current[scopeKey] = responseCursor;
          cursor = responseCursor;

          const sortedData = sortNewsByPublished(rawData);

          const uniqueItems = sortedData.filter((item) => {
            const key = getNewsKey(item);

            if (!key || existingKeys.has(key)) {
              return false;
            }

            existingKeys.add(key);
            return true;
          });

          collectedItems.push(...uniqueItems);

          /*
          |--------------------------------------------------------------------------
          | A short raw batch is the only normal end-of-feed signal.
          |--------------------------------------------------------------------------
          */
          hasMoreForScope =
            rawCount >= NEWS_CONFIG.loadMoreBatchSize;

          if (
            collectedItems.length > 0 ||
            !hasMoreForScope
          ) {
            break;
          }

          /*
          |--------------------------------------------------------------------------
          | Full duplicate batch:
          | keep walking with the advanced cursor, up to the safe hop limit.
          |--------------------------------------------------------------------------
          */
        }

        setHomeHasMoreByScope((prev) => ({
          ...prev,
          [scopeKey]: hasMoreForScope,
        }));

        homeLoadMoreFailureCountsRef.current[scopeKey] = 0;
        homeLoadMoreRetryAtRef.current[scopeKey] = 0;
        setHomeLoadMoreError("");

        if (collectedItems.length === 0) {
          return 0;
        }

        const updatedLinks = new Set(prevLinksRef.current);

        collectedItems.forEach((item) => {
          if (item?.link) {
            updatedLinks.add(item.link);
          }
        });

        loadedNewsKeysRef.current = existingKeys;
        prevLinksRef.current = updatedLinks;

        setAllNews((prev) =>
          mergeUniqueNewsByPublished(prev, collectedItems)
        );

        return collectedItems.length;
      } catch (error) {
        if (error?.name === "AbortError") {
          return 0;
        }

        const failureCount =
          (homeLoadMoreFailureCountsRef.current[scopeKey] || 0) + 1;
        const retryDelays = NEWS_CONFIG.retryDelaysMs;
        const retryDelay = retryDelays[
          Math.min(failureCount - 1, retryDelays.length - 1)
        ];

        homeLoadMoreFailureCountsRef.current[scopeKey] = failureCount;
        homeLoadMoreRetryAtRef.current[scopeKey] = Date.now() + retryDelay;

        setHomeLoadMoreError(
          error.message || "دریافت خبرهای بیشتر انجام نشد"
        );

        return 0;
      } finally {
        fetchingOlderScopesRef.current.delete(scopeKey);
      }
    },
    [homeHasMoreByScope]
  );

  /*
  |--------------------------------------------------------------------------
  | Read Home has-more state for an exact source scope.
  |--------------------------------------------------------------------------
  */
  const hasMoreHomeNewsForSources = useCallback(
    (sources = []) => {
      return homeHasMoreByScope[getOlderScopeKey(sources)] !== false;
    },
    [homeHasMoreByScope]
  );

  const canAttemptHomeLoadMoreForSources = useCallback((sources = []) => {
    const scopeKey = getOlderScopeKey(sources);
    return Date.now() >= (homeLoadMoreRetryAtRef.current[scopeKey] || 0);
  }, []);

  /*
  |--------------------------------------------------------------------------
  | Seed an isolated Focus feed from already-loaded Home items.
  |--------------------------------------------------------------------------
  |
  | The items are copied into Focus state. Later Focus pagination only mutates
  | focusNewsBySource and can never append anything to allNews.
  |
  */
  const seedFocusSourceNews = useCallback((source, seedItems = []) => {
    const normalizedSource = normalizeSourceName(source);
    const sourceStateKey = getSourceKey(normalizedSource);

    if (!normalizedSource || !sourceStateKey) {
      return;
    }

    const normalizedSeedItems = sortNewsByPublished(
      (seedItems || []).filter((item) => {
        return getSourceKey(item?.source) === sourceStateKey;
      })
    );

    if (normalizedSeedItems.length === 0) {
      return;
    }

    const nextKeys = new Set(
      focusNewsKeysBySourceRef.current[sourceStateKey] || []
    );

    normalizedSeedItems.forEach((item) => {
      const key = getNewsKey(item);

      if (key) {
        nextKeys.add(key);
      }
    });

    focusNewsKeysBySourceRef.current = {
      ...focusNewsKeysBySourceRef.current,
      [sourceStateKey]: nextKeys,
    };

    setFocusNewsBySource((prev) => {
      const currentItems = prev[sourceStateKey] || [];
      const mergedItems = mergeUniqueNewsByPublished(
        currentItems,
        normalizedSeedItems
      );

      if (mergedItems.length === currentItems.length) {
        return prev;
      }

      return {
        ...prev,
        [sourceStateKey]: mergedItems,
      };
    });
  }, []);

  /*
  |--------------------------------------------------------------------------
  | Commit accepted pending rows to the Unread model
  |--------------------------------------------------------------------------
  |
  | Polling only fills pendingNews. App calls this function for the exact rows
  | that are about to move from the capsule into the visible Home feed. Updating
  | both exact and fallback totals keeps the behavior correct whether or not the
  | source_counts metadata request succeeded.
  |
  */
  const commitPendingNewsToUnread = useCallback(
    (items = []) => {
      const committedCounts = countUnreadEligibleNewsBySource(
        items,
        unreadCountSince
      );

      if (Object.keys(committedCounts).length === 0) {
        return;
      }

      setSourceTotalCounts((previous) => {
        const next = { ...previous };
        let changed = false;

        Object.entries(committedCounts).forEach(([source, count]) => {
          if (!Object.prototype.hasOwnProperty.call(previous, source)) {
            return;
          }

          next[source] = (next[source] || 0) + count;
          changed = true;
        });

        return changed ? next : previous;
      });

      setUnreadFallbackCounts((previous) => {
        const next = { ...previous };

        Object.entries(committedCounts).forEach(([source, count]) => {
          next[source] = (next[source] || 0) + count;
        });

        return next;
      });
    },
    [unreadCountSince]
  );

  const commitPendingCountsToUnread = useCallback(
    (countsBySource = {}) => {
      const safeCounts = Object.entries(countsBySource).reduce(
        (result, [source, value]) => {
          const normalizedSource = normalizeSourceName(source);
          const count = Math.max(0, Math.floor(Number(value) || 0));

          if (normalizedSource && count > 0) {
            result[normalizedSource] =
              (result[normalizedSource] || 0) + count;
          }

          return result;
        },
        {}
      );

      if (Object.keys(safeCounts).length === 0) return;

      setSourceTotalCounts((previous) => {
        const next = { ...previous };
        let changed = false;

        Object.entries(safeCounts).forEach(([source, count]) => {
          if (!Object.prototype.hasOwnProperty.call(previous, source)) {
            return;
          }

          next[source] = (next[source] || 0) + count;
          changed = true;
        });

        return changed ? next : previous;
      });

      setUnreadFallbackCounts((previous) => {
        const next = { ...previous };

        Object.entries(safeCounts).forEach(([source, count]) => {
          next[source] = (next[source] || 0) + count;
        });

        return next;
      });
    },
    []
  );

  const clearLargeBacklog = useCallback(() => {
    setLargeBacklog(null);
  }, []);

  const replaceHomeFeedWithLatestSnapshot = useCallback(
    async ({ sources = [] } = {}) => {
      const normalizedSources = normalizeSourceList(sources);
      const scopeKey = getOlderScopeKey(normalizedSources);
      const requestToken =
        replaceHomeSnapshotRequestTokenRef.current + 1;

      replaceHomeSnapshotRequestTokenRef.current = requestToken;
      replaceHomeSnapshotControllerRef.current?.abort();

      const controller = new AbortController();
      replaceHomeSnapshotControllerRef.current = controller;
      setHomeSnapshotRefreshing(true);

      try {
        const response = await fetchLatestNews({
          cursor: { id: 0 },
          isInitial: true,
          signal: controller.signal,
          sources: normalizedSources,
        });

        if (requestToken !== replaceHomeSnapshotRequestTokenRef.current) {
          return {
            success: false,
            superseded: true,
            items: [],
          };
        }

        const now = Date.now();
        const items = sortNewsByPublished(response.items || [], now);
        const watermark = response.hasInsertWatermark
          ? response.insertWatermarkId
          : getLatestNewsId(response.items || []);

        insertCursorIdRef.current = Math.max(0, watermark);
        insertCatchupTargetIdRef.current = 0;
        insertPollHasMoreRef.current = false;

        /*
        | Drop every Home pagination cursor from the previous source scope.
        | Focus cursors stay isolated and are intentionally preserved.
        */
        olderCursorByScopeRef.current = Object.entries(
          olderCursorByScopeRef.current
        ).reduce((result, [storedScopeKey, cursor]) => {
          if (storedScopeKey.startsWith("focus:")) {
            result[storedScopeKey] = cursor;
          }

          return result;
        }, {});

        stalledOlderScopeCountsRef.current = Object.entries(
          stalledOlderScopeCountsRef.current
        ).reduce((result, [storedScopeKey, count]) => {
          if (storedScopeKey.startsWith("focus:")) {
            result[storedScopeKey] = count;
          }

          return result;
        }, {});

        fetchingOlderScopesRef.current = new Set(
          Array.from(fetchingOlderScopesRef.current).filter(
            (storedScopeKey) => storedScopeKey.startsWith("focus:")
          )
        );

        olderCursorByScopeRef.current[scopeKey] =
          getOldestNewsCursor(response.items || []);
        stalledOlderScopeCountsRef.current[scopeKey] = 0;
        homeLoadMoreFailureCountsRef.current = {};
        homeLoadMoreRetryAtRef.current = {};

        prevLinksRef.current = new Set(
          items.map((item) => item.link).filter(Boolean)
        );
        loadedNewsKeysRef.current = new Set(
          items.map(getNewsKey).filter(Boolean)
        );

        setAllNews(items);
        setPendingNews([]);
        setLargeBacklog(null);
        setHomeHasMoreByScope({
          [scopeKey]:
            (response.items || []).length >= NEWS_CONFIG.initialBatchSize,
        });
        setPollingError("");

        /*
        | Refresh exact source totals only after the snapshot replacement has
        | committed. This keeps the Unread model aligned with the new scope.
        */
        setSourceCountsRefreshVersion((version) => version + 1);

        return {
          success: true,
          superseded: false,
          items,
        };
      } catch (requestError) {
        if (requestError?.name === "AbortError") {
          return {
            success: false,
            superseded: true,
            items: [],
          };
        }

        if (isConnectionFailure(requestError)) {
          connectionUnavailableRef.current = true;
          setIsBrowserOffline(true);
        }

        setPollingError(
          requestError?.message || "دریافت خبرهای تازه انجام نشد"
        );

        return {
          success: false,
          superseded: false,
          items: [],
        };
      } finally {
        if (requestToken === replaceHomeSnapshotRequestTokenRef.current) {
          replaceHomeSnapshotControllerRef.current = null;
          setHomeSnapshotRefreshing(false);
        }
      }
    },
    []
  );

  const fetchFocusSourceNews = useCallback(
    async (
      source,
      currentItems = [],
      limit = NEWS_CONFIG.focusSourceInitialBatchSize,
      options = {}
    ) => {
      const normalizedSource = normalizeSourceName(source);
      const sourceStateKey = getSourceKey(normalizedSource);
      const isInitialSourceBatch = options.useCursor === false;

      if (
        !normalizedSource ||
        !sourceStateKey ||
        (
          !isInitialSourceBatch &&
          hasMoreNewsBySource[sourceStateKey] === false
        )
      ) {
        return 0;
      }

      if (
        !isInitialSourceBatch &&
        Date.now() < (focusLoadMoreRetryAtRef.current[sourceStateKey] || 0)
      ) {
        return 0;
      }

      if (focusFetchingSourcesRef.current.has(sourceStateKey)) {
        return 0;
      }

      const scopeKey = `focus:${sourceStateKey}`;

      focusFetchingSourcesRef.current.add(sourceStateKey);

      setLoadingFocusSources((prev) => ({
        ...prev,
        [sourceStateKey]: true,
      }));

      if (isInitialSourceBatch) {
        setFocusInitialErrorsBySource((prev) => ({
          ...prev,
          [sourceStateKey]: "",
        }));
      }

      let didCompleteSuccessfully = false;

      try {
        if (isInitialSourceBatch) {
          olderCursorByScopeRef.current[scopeKey] = {
            id: 0,
            sortDate: "",
          };

          stalledOlderScopeCountsRef.current[scopeKey] = 0;
        }

        let cursor =
          olderCursorByScopeRef.current[scopeKey] ||
          getOldestNewsCursor(currentItems);

        olderCursorByScopeRef.current[scopeKey] = cursor;

        const existingKeys = new Set(
          focusNewsKeysBySourceRef.current[sourceStateKey] || []
        );

        currentItems.forEach((item) => {
          const key = getNewsKey(item);

          if (key) {
            existingKeys.add(key);
          }
        });

        const collectedItems = [];
        let hasMoreForSource = true;

        for (
          let hop = 0;
          hop < MAX_DUPLICATE_BATCH_HOPS;
          hop += 1
        ) {
          const rawData = await fetchOlderNewsBatch({
            cursor,
            sources: [normalizedSource],
            limit,
          });

          const rawCount = rawData.length;

          if (rawCount === 0) {
            hasMoreForSource = false;
            break;
          }

          const responseCursor = getOldestNewsCursor(rawData);
          const cursorAdvanced = didCursorAdvance(
            cursor,
            responseCursor
          );

          if (!cursorAdvanced) {
            const stallCount =
              (stalledOlderScopeCountsRef.current[scopeKey] || 0) + 1;

            stalledOlderScopeCountsRef.current[scopeKey] = stallCount;
            hasMoreForSource = stallCount < MAX_STALLED_CURSOR_ATTEMPTS;
            break;
          }

          stalledOlderScopeCountsRef.current[scopeKey] = 0;
          olderCursorByScopeRef.current[scopeKey] = responseCursor;
          cursor = responseCursor;

          const sortedData = sortNewsByPublished(rawData);

          const uniqueItems = sortedData.filter((item) => {
            const key = getNewsKey(item);

            if (!key || existingKeys.has(key)) {
              return false;
            }

            existingKeys.add(key);
            return true;
          });

          collectedItems.push(...uniqueItems);
          hasMoreForSource = rawCount >= limit;

          if (collectedItems.length > 0 || !hasMoreForSource) {
            break;
          }
        }

        setHasMoreNewsBySource((prev) => ({
          ...prev,
          [sourceStateKey]: hasMoreForSource,
        }));

        focusLoadMoreFailureCountsRef.current[sourceStateKey] = 0;
        focusLoadMoreRetryAtRef.current[sourceStateKey] = 0;

        setFocusLoadMoreErrorsBySource((prev) => ({
          ...prev,
          [sourceStateKey]: "",
        }));

        if (isInitialSourceBatch) {
          setCheckedFocusSources((prev) => ({
            ...prev,
            [sourceStateKey]: true,
          }));

          setFocusInitialErrorsBySource((prev) => ({
            ...prev,
            [sourceStateKey]: "",
          }));
        }

        if (collectedItems.length === 0) {
          didCompleteSuccessfully = true;
          return 0;
        }

        focusNewsKeysBySourceRef.current = {
          ...focusNewsKeysBySourceRef.current,
          [sourceStateKey]: existingKeys,
        };

        setFocusNewsBySource((prev) => ({
          ...prev,
          [sourceStateKey]: mergeUniqueNewsByPublished(
            prev[sourceStateKey] || [],
            collectedItems
          ),
        }));

        didCompleteSuccessfully = true;
        return collectedItems.length;
      } catch (requestError) {
        if (requestError?.name === "AbortError") {
          return 0;
        }

        const message =
          requestError?.message || "دریافت خبرهای این منبع انجام نشد";

        if (isInitialSourceBatch) {
          setFocusInitialErrorsBySource((prev) => ({
            ...prev,
            [sourceStateKey]: message,
          }));

          setCheckedFocusSources((prev) => ({
            ...prev,
            [sourceStateKey]: false,
          }));
        } else {
          const failureCount =
            (focusLoadMoreFailureCountsRef.current[sourceStateKey] || 0) + 1;
          const retryDelays = NEWS_CONFIG.retryDelaysMs;
          const retryDelay = retryDelays[
            Math.min(failureCount - 1, retryDelays.length - 1)
          ];

          focusLoadMoreFailureCountsRef.current[sourceStateKey] = failureCount;
          focusLoadMoreRetryAtRef.current[sourceStateKey] =
            Date.now() + retryDelay;

          setFocusLoadMoreErrorsBySource((prev) => ({
            ...prev,
            [sourceStateKey]: message,
          }));
        }

        return 0;
      } finally {
        focusFetchingSourcesRef.current.delete(sourceStateKey);

        if (!didCompleteSuccessfully) {
          focusNewsKeysBySourceRef.current = {
            ...focusNewsKeysBySourceRef.current,
            [sourceStateKey]:
              focusNewsKeysBySourceRef.current[sourceStateKey] || new Set(),
          };
        }

        setLoadingFocusSources((prev) => ({
          ...prev,
          [sourceStateKey]: false,
        }));
      }
    },
    [hasMoreNewsBySource]
  );

  const retryFocusSourceNews = useCallback(
    async (
      source,
      currentItems = [],
      limit = NEWS_CONFIG.focusSourceInitialBatchSize
    ) => {
      const normalizedSource = normalizeSourceName(source);
      const sourceStateKey = getSourceKey(normalizedSource);

      if (!normalizedSource || !sourceStateKey) {
        return 0;
      }

      setFocusInitialErrorsBySource((prev) => ({
        ...prev,
        [sourceStateKey]: "",
      }));

      setCheckedFocusSources((prev) => ({
        ...prev,
        [sourceStateKey]: false,
      }));

      setHasMoreNewsBySource((prev) => ({
        ...prev,
        [sourceStateKey]: true,
      }));

      return fetchFocusSourceNews(
        normalizedSource,
        currentItems,
        limit,
        { useCursor: false }
      );
    },
    [fetchFocusSourceNews]
  );

  /*
  |--------------------------------------------------------------------------
  | Initial feed request and non-network retry
  |--------------------------------------------------------------------------
  |
  | Connection failures are recovered by the lightweight API probe above.
  | HTTP/server failures keep their normal backoff here. This prevents the app
  | from repeatedly downloading the full initial batch while the device is
  | disconnected.
  |
  */
  useEffect(() => {
    let disposed = false;
    let completed = false;
    let timeoutId = null;
    let activeController = null;
    let failureCount = 0;

    const clearTimer = () => {
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    const getRetryDelay = () => {
      const delays = NEWS_CONFIG.retryDelaysMs;

      return delays[
        Math.min(Math.max(failureCount - 1, 0), delays.length - 1)
      ];
    };

    const scheduleRetry = () => {
      if (disposed || completed) return;

      clearTimer();
      timeoutId = window.setTimeout(runInitialRequest, getRetryDelay());
    };

    const runInitialRequest = async () => {
      if (disposed || completed) return;

      activeController?.abort();
      activeController = new AbortController();

      const succeeded = await fetchNews(
        true,
        activeController.signal
      );

      activeController = null;

      if (disposed) return;

      if (succeeded) {
        completed = true;
        failureCount = 0;
        clearTimer();
        return;
      }

      failureCount += 1;

      if (connectionUnavailableRef.current) {
        return;
      }

      scheduleRetry();
    };

    const handleOnline = () => {
      window.dispatchEvent(new Event(CONNECTION_VERIFY_EVENT));
    };

    const handleOffline = () => {
      /*
      |-----------------------------------------------------------------------
      | Once the initial feed has loaded, polling/probe owns connectivity.
      |-----------------------------------------------------------------------
      |
      | The old code wrote to the blocking initial `error` even after initial
      | loading had completed. Its `online` handler then returned early because
      | `completed` was true, leaving the ticker stuck forever. Do not mutate
      | the initial error after completion.
      |
      */
      if (completed) return;

      connectionUnavailableRef.current = true;
      setIsBrowserOffline(true);
      clearTimer();
      activeController?.abort();
      activeController = null;
      setError("اتصال اینترنت خود را بررسی کنید");
      setLoading(false);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    runInitialRequest();

    return () => {
      disposed = true;
      clearTimer();
      activeController?.abort();

      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [fetchNews]);

  /*
  |--------------------------------------------------------------------------
  | Latest-news polling
  |--------------------------------------------------------------------------
  |
  | Polling stops while the real API connection probe is active. A successful
  | probe refreshes the feed immediately and changing `isBrowserOffline` back
  | to false restarts the normal polling schedule.
  |
  */
  useEffect(() => {
    /*
    | Keep one accepted Large Backlog snapshot stable until the user taps it.
    | New rows arriving meanwhile are captured by the fresh initial watermark
    | when Home is replaced, so continuing insert polling here would only
    | overwrite the displayed summary or create parallel catch-up sessions.
    */
    const hasVisibleLargeBacklog =
      (Number(largeBacklog?.freshCount) || 0) > 0;

    if (
      loading ||
      error ||
      isBrowserOffline ||
      hasVisibleLargeBacklog
    ) {
      return undefined;
    }

    let disposed = false;
    let timeoutId = null;
    let activeController = null;
    let failureCount = 0;

    const clearTimer = () => {
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    const getRetryDelay = () => {
      const delays = NEWS_CONFIG.retryDelaysMs;

      return delays[
        Math.min(Math.max(failureCount - 1, 0), delays.length - 1)
      ];
    };

    const schedule = (delay) => {
      if (disposed) return;

      clearTimer();
      timeoutId = window.setTimeout(runPollingRequest, delay);
    };

    const runPollingRequest = async () => {
      if (disposed) return;

      activeController?.abort();
      activeController = new AbortController();

      const succeeded = await fetchNews(
        false,
        activeController.signal
      );

      activeController = null;

      if (disposed) return;

      if (succeeded) {
        failureCount = 0;
        schedule(
          insertPollHasMoreRef.current
            ? 50
            : NEWS_CONFIG.refreshIntervalMs
        );
        return;
      }

      failureCount += 1;

      if (connectionUnavailableRef.current) {
        return;
      }

      schedule(getRetryDelay());
    };

    schedule(NEWS_CONFIG.refreshIntervalMs);

    return () => {
      disposed = true;
      clearTimer();
      activeController?.abort();
    };
  }, [
    error,
    fetchNews,
    isBrowserOffline,
    largeBacklog,
    loading,
  ]);

  return {
    allNews,
    setAllNews,
    loading,
    error,
    refreshError,
    pollingError,
    homeLoadMoreError,
    isBrowserOffline,
    sourceCatalog,
    sourceCatalogError,
    sourceCountsError,
    sourceTotalCounts,
    unreadFallbackCounts,
    unreadCountSince,
    availableSources,
    pendingNews,
    setPendingNews,
    largeBacklog,
    homeSnapshotRefreshing,
    clearLargeBacklog,
    setInsertPollingContext,
    replaceHomeFeedWithLatestSnapshot,
    commitPendingNewsToUnread,
    commitPendingCountsToUnread,
    focusNewsBySource,
    homeHasMoreByScope,
    hasMoreHomeNewsForSources,
    canAttemptHomeLoadMoreForSources,
    hasMoreNewsBySource,
    checkedFocusSources,
    failedFocusSources,
    focusInitialErrorsBySource,
    focusLoadMoreErrorsBySource,
    loadingFocusSources,
    fetchNews,
    fetchOlderNews,
    seedFocusSourceNews,
    fetchFocusSourceNews,
    retryFocusSourceNews,
  };
};
